/* ============================================================
   Karigur — Wedding Wardrobe Planner
   Date + ceremonies + who -> a backward-planned wardrobe timeline.
   Rules come from a JSON island the section serializes (editor-tunable);
   this file only computes and renders. The timing rules repeat the house
   lead times, which are owned by the Handbook (confirmed by the owner on
   5 Oct 2026): custom bridal takes three to four months from the date of
   deposit, fittings included, depending on the outfit and design; a
   groom's sherwani takes 2 to 4 months from the date of deposit. Fittings are delivery and
   fitting appointments, normally one or two, booked when the outfit arrives
   in store, so no fitting date is counted back from the event. Inputs
   persist in localStorage
   (outputs are recomputed on load, so rule changes propagate).
   Share URL: /pages/wedding-planner?date=YYYY-MM-DD&c=mehndi,nikkah&who=bride
   Vanilla JS, no deps.
   ============================================================ */
(function () {
  'use strict';

  var KEY = 'kb_wedding_plan_v1';
  var root = document.querySelector('[data-kb-planner]');
  if (!root) return;

  /* ---------------- rules ---------------- */
  var rules;
  try { rules = JSON.parse((root.querySelector('[data-kb-planner-rules]') || {}).textContent || '{}'); }
  catch (e) { rules = {}; }
  var CEREMONIES = rules.ceremonies || [];

  /* ---------------- helpers ---------------- */
  function track(name, detail) { try { if (window.kbTrack) window.kbTrack(name, detail || {}); } catch (e) {} }
  function readStore() { try { return JSON.parse(localStorage.getItem(KEY)) || null; } catch (e) { return null; } }
  function writeStore(p) { try { localStorage.setItem(KEY, JSON.stringify(p)); } catch (e) {} }
  function param(name) {
    var m = new RegExp('[?&]' + name + '=([^&]*)').exec(location.search);
    if (!m) return '';
    try { return decodeURIComponent(m[1].replace(/\+/g, ' ')); } catch (e) { return ''; } // a bad %-sequence reads as no value, not a broken page
  }
  function parseDate(str) {
    // Local noon avoids timezone off-by-one on date-only strings.
    if (!/^\d{4}-\d{2}-\d{2}$/.test(str || '')) return null;
    var p = str.split('-');
    var d = new Date(+p[0], +p[1] - 1, +p[2], 12, 0, 0);
    return isNaN(d.getTime()) ? null : d;
  }
  function addDays(d, n) { var x = new Date(d.getTime()); x.setDate(x.getDate() + n); return x; }
  var MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  function fmt(d) { return d.getDate() + ' ' + MONTHS[d.getMonth()] + ' ' + d.getFullYear(); }
  function daysUntil(d) { return Math.round((d.getTime() - Date.now()) / 86400000); }
  function esc(s) { return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
    return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
  }); }
  function tpl(str, map) { return String(str || '').replace(/\{(\w+)\}/g, function (_, k) { return map[k] != null ? map[k] : ''; }); }
  var NUM_WORDS = ['zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten', 'eleven', 'twelve'];
  function monthsPhrase(n) { return ((n === Math.round(n) && NUM_WORDS[n]) || String(n)) + (n === 1 ? ' month' : ' months'); }

  /* ---------------- plan model ---------------- */
  function readForm() {
    var date = (root.querySelector('[data-kb-plan-date]') || {}).value || '';
    var ceremonies = [];
    root.querySelectorAll('[data-kb-plan-cer]:checked').forEach(function (c) { ceremonies.push(c.value); });
    var whoEl = root.querySelector('[name="kb-plan-who"]:checked');
    return { date: date, ceremonies: ceremonies, who: whoEl ? whoEl.value : 'bride' };
  }
  function applyPlanToForm(plan) {
    var dateEl = root.querySelector('[data-kb-plan-date]');
    if (dateEl && plan.date) dateEl.value = plan.date;
    root.querySelectorAll('[data-kb-plan-cer]').forEach(function (c) {
      c.checked = plan.ceremonies.indexOf(c.value) !== -1;
    });
    var who = root.querySelector('[name="kb-plan-who"][value="' + (plan.who || 'bride') + '"]');
    if (who) who.checked = true;
  }
  function planFromParams() {
    var date = param('date');
    if (!parseDate(date)) return null;
    var known = CEREMONIES.map(function (c) { return c.key; });
    var cs = param('c').split(',').map(function (s) { return s.trim(); }).filter(function (s) { return known.indexOf(s) !== -1; });
    if (!cs.length) return null;
    var who = param('who');
    if (['bride', 'bride_groom', 'bride_family'].indexOf(who) === -1) who = 'bride';
    return { date: date, ceremonies: cs, who: who };
  }
  // A plan read back from localStorage is used only if it has the shape this page writes; anything else is ignored.
  function storedPlanIsUsable(p) {
    if (!p || typeof p !== 'object' || typeof p.date !== 'string' || !parseDate(p.date)) return false;
    if (!Array.isArray(p.ceremonies) || !p.ceremonies.length) return false;
    for (var i = 0; i < p.ceremonies.length; i++) if (typeof p.ceremonies[i] !== 'string') return false;
    if (['bride', 'bride_groom', 'bride_family'].indexOf(p.who) === -1) p.who = 'bride';
    return true;
  }
  function shareUrl(plan) {
    return location.origin + location.pathname + '?date=' + encodeURIComponent(plan.date) +
      '&c=' + encodeURIComponent(plan.ceremonies.join(',')) + '&who=' + encodeURIComponent(plan.who);
  }

  /* ---------------- milestones ---------------- */
  function ceremonyByKey(k) {
    for (var i = 0; i < CEREMONIES.length; i++) if (CEREMONIES[i].key === k) return CEREMONIES[i];
    return null;
  }
  function computeMilestones(plan) {
    var wed = parseDate(plan.date);
    if (!wed) return { state: 'invalid', milestones: [] };
    var today = new Date(); today.setHours(12, 0, 0, 0);
    var daysOut = daysUntil(wed);
    if (daysOut < 0) return { state: 'past', milestones: [] };
    if (daysOut < (rules.ready_min_weeks || 2) * 7) return { state: 'urgent', milestones: [] };

    var ms = [];
    var monthsOut = daysOut / 30.4;
    var overallPath = 'custom';

    // The house rule: custom bridal takes three to four months from the date of deposit,
    // fittings included, depending on the outfit and design. Four months or more to the
    // ceremony is comfortable; three to four is possible, depending on the outfit and design;
    // under three is ready bridal, or a conversation.
    var comfortMonths = rules.custom_comfort_months || 4;
    var possibleMonths = rules.custom_possible_months || 3;
    var leadCopy = rules.copy_lead || 'Custom bridal takes three to four months from the date of deposit, fittings included, depending on the outfit and design.';
    // Fittings are delivery and fitting appointments, booked as soon as the outfit arrives
    // in store (owner, 5 Oct 2026): normally one or two, the second only if alterations are
    // needed. Nothing is counted back from the event, so this card carries no date.
    function fittingsCard(cd, label) {
      return {
        when: null, whenLabel: rules.copy_fittings_when || 'When your outfit arrives in store', sort: cd.getTime() - 3e9, cer: label,
        title: 'Delivery and fitting for your ' + label + ' look',
        body: rules.copy_fittings || 'We book your delivery and fitting appointment as soon as your outfit arrives in store. If it fits well, you take it home that day. If it needs alterations, there is usually one more visit. All of it sits inside the three to four months.',
        href: rules.booking_url || '/pages/book-consultation', link: 'Book a Bridal Consultation'
      };
    }

    // 1) Book the consultation — always first, dated now.
    ms.push({
      when: today, sort: -1e9, cer: '',
      title: rules.copy_book_title || 'Book your Bridal Consultation',
      body: rules.copy_book_body || 'Everything below flows from one conversation about your ceremonies, your world of colour, and your timeline.',
      href: rules.booking_url || '/pages/book-consultation', link: 'Book a Bridal Consultation'
    });

    var chosen = plan.ceremonies.map(ceremonyByKey).filter(Boolean);
    var earliest = null;

    chosen.forEach(function (c) {
      var cd = addDays(wed, c.offset_days || 0);
      if (!earliest || cd < earliest) earliest = cd;
      var cDaysOut = daysUntil(cd);
      var cMonths = cDaysOut / 30.4;
      var label = c.label || c.key;
      var col = c.collection_link || '/collections/bridals';

      if (c.imprecise) {
        ms.push({
          when: null, sort: cd.getTime() - 8.6e9, cer: label,
          title: label + ', commonly held in the months before',
          body: 'If yours is already scheduled, plan its look first. Lighter pieces suit it beautifully.',
          href: col, link: 'Explore looks'
        });
        return;
      }

      if (!c.full_look) {
        ms.push({
          when: addDays(cd, -((rules.buffer_days || 7) + 14)), sort: cd.getTime() - 2e9, cer: label,
          title: 'Choose your ' + label + ' look',
          body: 'A lighter occasion: ready pieces and luxury pret carry it well. Have it in hand a couple of weeks ahead.',
          href: col, link: label + ' looks'
        });
        return;
      }

      // Full bridal look: custom vs ready verdict per ceremony.
      if (cMonths >= comfortMonths) {
        // Comfortable: the deposit date that leaves the full four months.
        var depositBy = addDays(cd, -Math.round(comfortMonths * 30.4));
        if (depositBy < today) depositBy = today;
        ms.push({
          when: depositBy, sort: depositBy.getTime(), cer: label,
          title: 'Place the deposit for your custom ' + label + ' look',
          body: leadCopy + ' A deposit by this date leaves comfortable time.',
          href: rules.custom_url || '/pages/custom-bridal', link: 'How custom works'
        });
        ms.push(fittingsCard(cd, label));
      } else if (cMonths >= possibleMonths) {
        // Possible, depending on the outfit and design: the deposit date matters now.
        ms.push({
          when: today, sort: today.getTime() + 1, cer: label,
          title: 'Custom for your ' + label + ' is possible, depending on the outfit and design',
          body: leadCopy + ' Your ' + label + ' falls inside that time, so the date of your deposit matters now. A consultation tells you honestly whether your design fits. Ready bridal is the other path.',
          href: rules.booking_url || '/pages/book-consultation', link: 'Book a Bridal Consultation'
        });
        ms.push(fittingsCard(cd, label));
      } else {
        // Under the lead time: ready bridal, or a conversation.
        overallPath = 'ready';
        ms.push({
          when: today, sort: today.getTime() + 1, cer: label,
          title: tpl(rules.copy_tight_title || 'Ready bridal is the honest path for your {ceremony}', { ceremony: label, date: fmt(cd) }),
          body: tpl(rules.copy_tight_body || 'Custom bridal takes three to four months from the date of deposit, fittings included, so a custom {ceremony} look would have needed its deposit by {date}. Finished pieces, fitted to you, honour this timeline beautifully. If your heart is set on custom, talk to us.', { ceremony: label, date: fmt(addDays(cd, -Math.round(possibleMonths * 30.4))) }),
          href: rules.ready_url || '/pages/ready-bridal', link: 'How ready bridal works'
        });
        ms.push({
          when: null, whenLabel: 'Before your day', sort: cd.getTime() - 2.5e9, cer: label,
          title: 'Fitting for your ' + label + ' look',
          body: 'Ready pieces are fitted to you at the flagship, typically in a session or two.',
          href: rules.booking_url || '/pages/book-consultation', link: 'Book a fitting'
        });
      }
    });

    // Jewellery — one milestone off the earliest full ceremony.
    if (earliest) {
      ms.push({
        when: addDays(earliest, -((rules.jewellery_max_weeks || 8) * 7)), sort: earliest.getTime() - 4e9, cer: '',
        title: 'Choose or commission your jewellery',
        body: 'Matched sets take ' + (rules.jewellery_min_weeks || 5) + ' to ' + (rules.jewellery_max_weeks || 8) + ' weeks when customised. Choosing alongside the outfit keeps tones and scale as one.',
        href: '/collections/jewelry', link: 'Explore jewellery'
      });
    }

    // Groom — when she is dressing him too.
    // A made-to-order sherwani takes 2 to 4 months from deposit (owner, 16 Oct 2026): start by the longer end,
    // "now" inside the window, and under the shorter end the honest card is ready-to-wear.
    if (plan.who === 'bride_groom' && earliest) {
      var groomMonths = rules.groom_min_months || 2;
      var groomMax = rules.groom_max_months || 4;
      var groomBy = addDays(earliest, -Math.round(groomMax * 30.4));
      var groomLatest = addDays(earliest, -Math.round(groomMonths * 30.4));
      if (groomBy < today && groomLatest >= today) groomBy = today; // inside the 2-to-4-month window: begin now
      if (groomBy >= today) {
        ms.push({
          when: groomBy, sort: earliest.getTime() - 4.5e9, cer: '',
          title: 'Begin the groom’s sherwani',
          body: 'His look is planned next to yours so the pair photographs as one. A made-to-order sherwani takes ' + groomMonths + ' to ' + groomMax + ' months from the date of deposit.',
          href: '/pages/groom', link: 'Explore groom'
        });
      } else {
        ms.push({
          when: today, sort: today.getTime() + 2, cer: '',
          title: 'The groom, on a shorter runway',
          body: 'A made-to-order sherwani takes at least ' + monthsPhrase(groomMonths) + '. With less time than that, ask about ready-to-wear groom pieces in store.',
          href: '/pages/groom', link: 'Explore groom'
        });
      }
    }

    // Family — one honest card, not an invented timeline.
    if (plan.who === 'bride_family') {
      ms.push({
        when: null, sort: (earliest || wed).getTime() - 1e9, cer: '',
        title: 'Family and guests, closer to the dates',
        body: 'Ready-to-wear and luxury pret carry the family beautifully and need no long lead time. Shop a few weeks out, once your own look is settled.',
        href: '/collections/luxury-pret', link: 'Shop ready-to-wear'
      });
    }

    // Clamp past-dated milestones to "now" with honest copy.
    ms.forEach(function (m) {
      if (m.when && m.when < today) { m.overdue = true; m.when = today; }
    });
    ms.sort(function (a, b) { return (a.when ? a.when.getTime() : a.sort) - (b.when ? b.when.getTime() : b.sort) || a.sort - b.sort; });

    return { state: 'ok', milestones: ms, monthsOut: Math.round(monthsOut), path: overallPath, generous: monthsOut > 24 };
  }

  /* ---------------- render ---------------- */
  var out = root.querySelector('[data-kb-plan-out]');
  var formWrap = root.querySelector('[data-kb-plan-formwrap]');
  var current = null;

  function summaryLine(plan, res) {
    var names = plan.ceremonies.map(function (k) { var c = ceremonyByKey(k); return c ? (c.label || k) : k; });
    return res.milestones.filter(function (m) { return m.when; }).slice(0, 6).map(function (m) {
      return m.title + ' by ' + fmt(m.when);
    }).join('; ') + (names.length ? ' | Ceremonies: ' + names.join(', ') : '');
  }

  function render(plan, opts) {
    opts = opts || {};
    var res = computeMilestones(plan);
    if (!out) return;
    if (res.state === 'invalid') { out.innerHTML = ''; return; }

    if (res.state === 'past') {
      out.innerHTML = '<div class="kb-plan__edge"><p>' + esc(rules.copy_past || 'That date has already passed. If the celebration is still ahead, check the year. If it was recent, congratulations, and our door stays open for what comes next.') + '</p>' +
        '<a class="kb-btn kb-btn--primary" href="' + esc(rules.booking_url || '/pages/book-consultation') + '">Book a Bridal Consultation</a></div>';
      show(true); return;
    }
    if (res.state === 'urgent') {
      out.innerHTML = '<div class="kb-plan__edge"><p>' + esc(rules.copy_urgent || 'Under two weeks out, the honest answer is a conversation, not a checklist. Call the flagship or book now and we will tell you exactly what is possible.') + '</p>' +
        '<a class="kb-btn kb-btn--primary" href="' + esc(rules.booking_url || '/pages/book-consultation') + '">Book now</a></div>';
      show(true); return;
    }

    var html = '<p class="kb-plan__intro-line">' + esc(res.monthsOut) + ' months out' + (res.generous ? ', a generous runway' : '') + '. Working backward from ' + esc(fmt(parseDate(plan.date))) + ':</p>';
    html += '<ol class="kb-plan__timeline" role="list">';
    res.milestones.forEach(function (m) {
      html += '<li class="kb-plan__ms' + (m.overdue ? ' kb-plan__ms--now' : '') + '">' +
        '<p class="kb-plan__ms-when">' + (m.when ? (m.overdue ? 'Now, this week' : 'By ' + esc(fmt(m.when))) : esc(m.whenLabel || 'When it suits')) + (m.cer ? ' · ' + esc(m.cer) : '') + '</p>' +
        '<h3 class="kb-plan__ms-title">' + esc(m.title) + '</h3>' +
        '<p class="kb-plan__ms-body">' + esc(m.body) + '</p>' +
        (m.href ? '<a class="kb-plan__ms-link kb-ulink" href="' + esc(m.href) + '">' + esc(m.link || 'Learn more') + '</a>' : '') +
        '</li>';
    });
    html += '</ol>';
    out.innerHTML = html;
    show(true);

    // Sync capture + share + persistence.
    var f = {
      date: root.querySelector('[data-kb-cap-date]'),
      cer: root.querySelector('[data-kb-cap-cer]'),
      who: root.querySelector('[data-kb-cap-who]'),
      sum: root.querySelector('[data-kb-cap-summary]'),
      url: root.querySelector('[data-kb-cap-url]')
    };
    var names = plan.ceremonies.map(function (k) { var c = ceremonyByKey(k); return c ? (c.label || k) : k; }).join(', ');
    if (f.date) f.date.value = plan.date;
    if (f.cer) f.cer.value = names;
    if (f.who) f.who.value = plan.who;
    if (f.sum) f.sum.value = summaryLine(plan, res).slice(0, 500);
    if (f.url) f.url.value = shareUrl(plan);

    if (!opts.silent) {
      writeStore(plan);
      track('planner_complete', { kb_months_out: res.monthsOut, kb_ceremonies: plan.ceremonies.join(','), kb_path: res.path });
    }
    current = plan;
  }

  function show(hasPlan) {
    var actions = root.querySelector('[data-kb-plan-actions]');
    if (actions) actions.hidden = !hasPlan;
  }

  /* ---------------- wire ---------------- */
  var form = root.querySelector('[data-kb-plan-form]');
  var started = false;
  if (form) {
    form.addEventListener('change', function () {
      if (!started) { started = true; track('planner_start', {}); }
    });
    form.addEventListener('submit', function (e) {
      e.preventDefault();
      var plan = readForm();
      var err = root.querySelector('[data-kb-plan-error]');
      if (!plan.date || !parseDate(plan.date)) { if (err) { err.hidden = false; err.textContent = 'Choose your wedding date first.'; } return; }
      if (!plan.ceremonies.length) { if (err) { err.hidden = false; err.textContent = 'Pick at least one ceremony.'; } return; }
      if (err) err.hidden = true;
      render(plan);
      var first = out.querySelector('.kb-plan__ms-title'); if (first) { first.setAttribute('tabindex', '-1'); first.focus(); }
    });
  }

  var shareBtn = root.querySelector('[data-kb-plan-share]');
  if (shareBtn) shareBtn.addEventListener('click', function () {
    if (!current) return;
    var url = shareUrl(current);
    track('planner_share', {});
    var lbl = shareBtn.querySelector('[data-kb-plan-share-label]') || shareBtn;
    if (navigator.share) { navigator.share({ title: 'Our wedding wardrobe plan', url: url }).catch(function () {}); }
    else if (navigator.clipboard) {
      navigator.clipboard.writeText(url).then(function () {
        var o = lbl.textContent; lbl.textContent = 'Link copied'; setTimeout(function () { lbl.textContent = o; }, 2000);
      }).catch(function () { window.prompt('Copy your plan link:', url); });
    } else { window.prompt('Copy your plan link:', url); }
  });

  var editBtn = root.querySelector('[data-kb-plan-edit]');
  if (editBtn) editBtn.addEventListener('click', function () {
    if (formWrap) formWrap.hidden = false;
    editBtn.hidden = true;
    var d = root.querySelector('[data-kb-plan-date]'); if (d) d.focus();
  });

  var capForm = root.querySelector('[data-kb-plan-capture]');
  if (capForm) capForm.addEventListener('submit', function () { track('planner_lead', { kb_months_out: current ? computeMilestones(current).monthsOut : '' }); });

  /* ---------------- boot ---------------- */
  var fromUrl = planFromParams();
  var stored = readStore();
  if (fromUrl) {
    applyPlanToForm(fromUrl);
    render(fromUrl, { silent: true });
    var mine = readStore();
    var seed = root.querySelector('[data-kb-plan-adopt]');
    if (seed && (!mine || JSON.stringify(mine) !== JSON.stringify(fromUrl))) {
      seed.hidden = false;
      seed.addEventListener('click', function () { writeStore(fromUrl); seed.hidden = true; track('planner_adopt', {}); });
    }
    if (formWrap) formWrap.hidden = true;
    if (editBtn) editBtn.hidden = false;
  } else if (storedPlanIsUsable(stored)) {
    applyPlanToForm(stored);
    render(stored, { silent: true });
    if (formWrap) formWrap.hidden = true;
    if (editBtn) editBtn.hidden = false;
  }
})();
